import { ByteReader, ByteWriter } from "./bytes.js";

/**
 * Schema-driven binary codec for snapshots (ADR 0011). A game describes its state once and gets
 * encode/decode with two-way version compatibility (ADR 0004: rollouts run mixed versions, rollback =
 * older code reading newer state):
 *
 * - Fields are only ever APPENDED to a struct, marked `since: N` with a `default`.
 * - Every struct is length-prefixed, so code at version N-1 skips fields it doesn't know (reads N),
 *   and code at version N fills fields missing from N-1 data with their defaults (reads N-1).
 * - Never remove, reorder or retype a field: a retired field keeps being written (any value old code
 *   accepts). Raising an array's `max` takes two releases: readers first, writers after.
 *
 * Persisted snapshots (restore after a DO restart) must be LOSSLESS: use exact integer types, and keep
 * simulation state in integer/fixed-point units. `quant`/`angle8` are for client view snapshots, or for
 * values the simulation already keeps quantised.
 */
export interface Codec<T> {
  /** Snapshot version that introduced this field (when used inside a struct). */
  readonly since: number;
  /** Highest `since` within this codec (structs/arrays look inside). */
  readonly maxSince: number;
  /** Value for this field when reading data written before `since`. */
  fallback(): T;
  write(w: ByteWriter, value: T): void;
  read(r: ByteReader, version: number): T;
}

export type Infer<C> = C extends Codec<infer T> ? T : never;

export interface FieldOptions<T> {
  /** Snapshot version that added this field (default 1). Fields after version 1 need a default. */
  since?: number;
  /** Used when reading data that predates `since`. Use a function for arrays/objects (fresh value each time). */
  default?: T | (() => T);
}

/** `since` and the fallback function for a field, validating that later fields have a default. */
function versioning<T>(opts: FieldOptions<T> | undefined): { since: number; fallback: () => T } {
  const since = opts?.since ?? 1;
  const def = opts?.default;
  if (!Number.isInteger(since) || since < 1)
    throw new Error(`since must be a positive integer: ${since}`);
  if (since > 1 && def === undefined)
    throw new Error(`a field added in version ${since} needs a default`);
  return {
    since,
    fallback: () => {
      if (def === undefined) throw new Error("field missing from snapshot and has no default");
      return typeof def === "function" ? (def as () => T)() : def;
    },
  };
}

function leaf<T>(
  write: (w: ByteWriter, v: T) => void,
  read: (r: ByteReader) => T,
  opts?: FieldOptions<T>,
): Codec<T> {
  const { since, fallback } = versioning(opts);
  return { since, maxSince: since, fallback, write, read };
}

const intIn = (v: number, lo: number, hi: number, kind: string) => {
  if (!Number.isInteger(v) || v < lo || v > hi) throw new RangeError(`${kind} out of range: ${v}`);
};
/** NaN/Infinity in a lossy field is a simulation bug; fail loudly instead of sending 0. */
const finite = (v: number, kind: string) => {
  if (!Number.isFinite(v)) throw new RangeError(`${kind} needs a finite number: ${v}`);
};

export const u8 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      intIn(v, 0, 0xff, "u8");
      w.u8(v);
    },
    (r) => r.u8(),
    o,
  );
export const u16 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      intIn(v, 0, 0xffff, "u16");
      w.u16(v);
    },
    (r) => r.u16(),
    o,
  );
export const u32 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      intIn(v, 0, 0xffffffff, "u32");
      w.u32(v);
    },
    (r) => r.u32(),
    o,
  );
export const i16 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      intIn(v, -0x8000, 0x7fff, "i16");
      w.i16(v);
    },
    (r) => r.i16(),
    o,
  );
export const i32 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      intIn(v, -0x80000000, 0x7fffffff, "i32");
      w.i32(v);
    },
    (r) => r.i32(),
    o,
  );
export const varuint = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      w.varuint(v);
    },
    (r) => r.varuint(),
    o,
  );
/** LOSSY for most numbers (single precision). Prefer integers or `quant`. */
export const f32 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      w.f32(v);
    },
    (r) => r.f32(),
    o,
  );
export const f64 = (o?: FieldOptions<number>) =>
  leaf<number>(
    (w, v) => {
      w.f64(v);
    },
    (r) => r.f64(),
    o,
  );
export const bool = (o?: FieldOptions<boolean>) =>
  leaf<boolean>(
    (w, v) => {
      w.u8(v ? 1 : 0);
    },
    (r) => r.u8() !== 0,
    o,
  );

/**
 * LOSSY: a number in [min, max] stored in 8 or 16 bits (clamped). Max error = (max - min) / (2^bits - 1) / 2.
 * P1-S12: positions as quant(0, 640, 16) ≈ 0.005-unit precision.
 */
export function quant(
  min: number,
  max: number,
  bits: 8 | 16,
  o?: FieldOptions<number>,
): Codec<number> {
  const steps = 2 ** bits - 1;
  const scale = steps / (max - min);
  return leaf<number>(
    (w, v) => {
      finite(v, "quant");
      const q = Math.round((Math.min(max, Math.max(min, v)) - min) * scale);
      if (bits === 8) w.u8(q);
      else w.u16(q);
    },
    (r) => min + (bits === 8 ? r.u8() : r.u16()) / scale,
    o,
  );
}

/** LOSSY: an angle in radians stored in one byte (1/256 turn). Reads back in [-π, π). */
export function angle8(o?: FieldOptions<number>): Codec<number> {
  const TAU = 2 * Math.PI;
  return leaf<number>(
    (w, v) => {
      finite(v, "angle8");
      w.u8(Math.round(((((v % TAU) + TAU) % TAU) / TAU) * 256) & 0xff);
    },
    (r) => {
      const a = (r.u8() / 256) * TAU;
      return a >= Math.PI ? a - TAU : a;
    },
    o,
  );
}

/** Raw bytes (e.g. a game's own snapshot inside the runner's), at most `max` long. */
export function bytes(opts: { max: number } & FieldOptions<Uint8Array>): Codec<Uint8Array> {
  return leaf<Uint8Array>(
    (w, v) => {
      if (v.length > opts.max)
        throw new RangeError(`bytes longer than max ${opts.max}: ${v.length}`);
      w.varuint(v.length);
      w.bytes(v);
    },
    (r) => {
      const n = r.varuint();
      if (n > opts.max) throw new RangeError(`bytes longer than max ${opts.max}: ${n}`);
      return r.bytes(n).slice();
    },
    opts,
  );
}

/** A list of `of`, at most `max` long (also enforced when decoding untrusted bytes). */
export function array<T>(of: Codec<T>, opts: { max: number } & FieldOptions<T[]>): Codec<T[]> {
  const { since, fallback } = versioning(opts);
  return {
    since,
    maxSince: Math.max(since, of.maxSince),
    fallback,
    write(w, values) {
      if (values.length > opts.max)
        throw new RangeError(`array longer than max ${opts.max}: ${values.length}`);
      w.varuint(values.length);
      for (const v of values) of.write(w, v);
    },
    read(r, version) {
      const n = r.varuint();
      if (n > opts.max) throw new RangeError(`array longer than max ${opts.max}: ${n}`);
      const out: T[] = [];
      for (let i = 0; i < n; i++) out.push(of.read(r, version));
      return out;
    },
  };
}

type Shape = Record<string, Codec<unknown>>;
export type StructOf<S extends Shape> = { [K in keyof S]: Infer<S[K]> };

/**
 * A record of named fields, written in declaration order with a byte-length prefix. New fields must be
 * declared at the end with a higher `since` (checked here), which is what keeps old and new code compatible.
 */
export function struct<S extends Shape>(
  shape: S,
  opts?: FieldOptions<StructOf<S>>,
): Codec<StructOf<S>> {
  const fields = Object.entries(shape);
  let prev = 1;
  for (const [name, c] of fields) {
    if (c.since < prev)
      throw new Error(
        `field "${name}" (since ${c.since}) is declared after a field from version ${prev}; new fields go at the end`,
      );
    prev = c.since;
  }
  const { since, fallback } = versioning(opts);
  return {
    since,
    maxSince: Math.max(since, ...fields.map(([, c]) => c.maxSince)),
    fallback,
    write(w, value) {
      const start = w.beginSection();
      for (const [name, c] of fields) c.write(w, (value as Record<string, unknown>)[name]);
      w.endSection(start);
    },
    read(r, version) {
      const outer = r.beginSection(r.varuint());
      const out: Record<string, unknown> = {};
      for (const [name, c] of fields) {
        out[name] = c.since > version ? c.fallback() : c.read(r, version);
      }
      r.endSection(outer); // skips trailing fields from newer versions
      return out as StructOf<S>;
    },
  };
}

export const SNAPSHOT_FORMAT = 1;

export interface SnapshotCodec<T> {
  readonly version: number;
  encode(value: T): Uint8Array;
  /** Decodes snapshots of this version, older ones (defaults fill new fields) and newer ones (unknown fields skipped). */
  decode(bytes: Uint8Array): { version: number; value: T };
}

/** Wraps a root codec with a header: u8 format | varuint snapshotVersion | root. */
export function defineSnapshot<T>(root: Codec<T>, opts: { version: number }): SnapshotCodec<T> {
  if (root.maxSince > opts.version) {
    throw new Error(
      `schema has fields from version ${root.maxSince} but snapshot version is ${opts.version}`,
    );
  }
  return {
    version: opts.version,
    encode(value) {
      const w = new ByteWriter();
      w.u8(SNAPSHOT_FORMAT);
      w.varuint(opts.version);
      root.write(w, value);
      return w.finish();
    },
    decode(bytes) {
      const r = new ByteReader(bytes);
      const format = r.u8();
      if (format !== SNAPSHOT_FORMAT) throw new Error(`snapshot format ${format} not supported`);
      const version = r.varuint();
      const value = root.read(r, version);
      if (r.position !== r.end) throw new Error("trailing bytes after snapshot");
      return { version, value };
    },
  };
}
